import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Search, Download, Trash2, Users, PhoneCall, BadgeCheck, CircleDot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "qualified", "closed"];

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("brandify_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers, params }),
        axios.get(`${API}/leads/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("brandify_admin_token");
        setToken("");
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, q, status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("brandify_admin_token", data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("brandify_admin_token");
    setToken("");
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status: newStatus }, { headers });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
      toast.success(`Lead marked as ${newStatus}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Could not delete lead.");
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Phone", "Email", "Company", "Service", "Budget", "City", "Status", "Message", "Date"]];
    leads.forEach((l) => rows.push([l.name, l.phone, l.email || "", l.company, l.service, l.budget, l.city, l.status, l.message.replace(/\n/g, " "), l.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `brandify-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-400/60 transition-colors";

  if (!token) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="admin-login-page">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={login}
          className="glow-border-strong rounded-3xl p-8 w-full max-w-sm"
          data-testid="admin-login-form"
        >
          <div className="mb-8">
            <img src="/assets/brandify-logo.png" alt="Brandify" className="h-10 w-auto" />
            <p className="text-[10px] text-zinc-500 uppercase tracking-[0.25em] mt-3">Admin Console</p>
          </div>
          <input type="email" required placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-4`} data-testid="admin-email-input" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mb-6`} data-testid="admin-password-input" />
          <button type="submit" disabled={loginLoading} data-testid="admin-login-submit-btn" className="btn-gradient w-full text-white font-semibold py-3.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loginLoading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
          </button>
          <a href="/" className="block text-center mt-5 text-xs text-zinc-500 hover:text-white transition-colors">← Back to website</a>
        </motion.form>
      </div>
    );
  }

  const statCards = [
    { label: "Total Leads", value: stats?.total ?? 0, icon: Users, id: "total" },
    { label: "New", value: stats?.new ?? 0, icon: CircleDot, id: "new" },
    { label: "Contacted", value: stats?.contacted ?? 0, icon: PhoneCall, id: "contacted" },
    { label: "Qualified", value: stats?.qualified ?? 0, icon: BadgeCheck, id: "qualified" },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/brandify-logo.png" alt="Brandify" className="h-8 w-auto" />
            <span className="text-xs text-zinc-500 tracking-[0.2em] uppercase">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-zinc-400 hover:text-white transition-colors hidden sm:block">View site</a>
            <button data-testid="admin-logout-btn" onClick={logout} className="glass text-xs font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2 hover:border-indigo-400/50 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map(({ label, value, icon: Icon, id }) => (
            <div key={id} className="glass rounded-2xl p-5" data-testid={`admin-stat-${id}`}>
              <Icon className="w-5 h-5 text-indigo-400 mb-3" />
              <p className="font-display text-3xl font-extrabold">{value}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">{label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input placeholder="Search name, phone, company..." value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-11`} data-testid="admin-search-input" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} sm:w-48 appearance-none`} data-testid="admin-status-filter">
            <option value="" className="bg-zinc-900">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="btn-gradient text-sm font-semibold px-5 py-3 rounded-xl inline-flex items-center justify-center gap-2 whitespace-nowrap">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="glass rounded-2xl overflow-x-auto" data-testid="admin-leads-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
                {["Lead", "Contact", "Service / Budget", "Message", "Date", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && leads.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">No leads yet — share your site and they'll appear here.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-white">{l.name}</p>
                    <p className="text-xs text-zinc-500">{l.company || "—"}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-zinc-300">{l.phone}</p>
                    <p className="text-xs text-zinc-500">{l.email || "—"}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-zinc-300">{l.service || "General"}</p>
                    <p className="text-xs text-zinc-500">{l.budget || "—"}</p>
                  </td>
                  <td className="px-5 py-4 max-w-[220px]"><p className="text-xs text-zinc-400 truncate" title={l.message}>{l.message || "—"}</p></td>
                  <td className="px-5 py-4 text-xs text-zinc-500 whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</td>
                  <td className="px-5 py-4">
                    <select
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      data-testid={`admin-lead-status-${l.id}`}
                      className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-400/60 appearance-none"
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <button onClick={() => remove(l.id)} data-testid={`admin-lead-delete-${l.id}`} className="text-zinc-600 hover:text-red-400 transition-colors" aria-label="Delete lead">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default Admin;
